/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Flex, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import PromoCallout from '../product-tile/promo-callout'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {StarredBrandLogo} from '../icons/index'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useProductsById} from '../../pages/checkout/util/utils'
import {getIsFixedPriceModel} from '../../esw/esw-helpers'
import useIsInternational from '../../hooks/use-is-international'

const CartItems = ({basket, showHeading = true}) => {
    const config = getConfig()
    const isFixedPriceModel = getIsFixedPriceModel()
    const isInternational = useIsInternational()
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products} = useProductsById(productIds)
    return (
        <Stack spacing={5} align="flex-start" width="100%">
            {showHeading && (
                <Text fontSize="xl" textTransform="uppercase" fontWeight="700">
                    <FormattedMessage defaultMessage="Your Bag" id="order_summary.label.your_bag" />
                </Text>
            )}
            {basket.productItems?.map((product, idx) => {
                const variant = {
                    ...product,
                    ...(products && products[product.productId]),
                    price: product.price,
                    c_eswShopperCurrency: basket.c_eswShopperCurrency,
                    c_isFixedPriceModel: isFixedPriceModel
                }

                let enableFinalSaleLabel =
                    !isInternational &&
                    (config.app.ENABLE_FINAL_SALE_LABEL === true ||
                        config.app.ENABLE_FINAL_SALE_LABEL === 'true' ||
                        product?.c_isEGiftCard ||
                        product?.c_isGiftCard)

                return (
                    <ItemVariantProvider key={product.productId} variant={variant}>
                        <Flex width="full" height="full">
                            <CartItemVariantImage
                                width="90px"
                                height="100%"
                                mr={4}
                                ratio={2 / 3}
                                isGiftCard={product?.c_isEGiftCard || product?.c_isGiftCard}
                                index={idx}
                            />
                            <Stack spacing={0}>
                                <StarredBrandLogo width="43px" height="8px" mb={2} />
                                <CartItemVariantName />
                                <CartItemVariantAttributes flex="none" />
                                <Flex columnGap="5px">
                                    <Text fontSize="sm">
                                        <FormattedMessage
                                            defaultMessage="{quantity} x "
                                            id="checkout_bag.item_quantity"
                                            values={{
                                                quantity: variant.quantity
                                            }}
                                        />
                                    </Text>
                                    <CartItemVariantPrice
                                        fontSize="sm"
                                        currency={basket?.currency}
                                        isDisplayPricePerUnit={true}
                                    />
                                </Flex>
                                {!isInternational && variant?.productPromotions && (
                                    <Text color="gray.700" fontSize="sm">
                                        <PromoCallout product={variant} />
                                    </Text>
                                )}
                                {variant?.c_isFinalSale && enableFinalSaleLabel && (
                                    <Text color="gray.700" fontSize="sm">
                                        <FormattedMessage
                                            defaultMessage="Final Sale*"
                                            id="display_price.label.final_sale"
                                        />
                                    </Text>
                                )}
                                <HideOnMobile>
                                    <Flex alignItems="center" fontSize="sm" fontWeight="700">
                                        <Text mr={1}>Item total:</Text>
                                        <CartItemVariantPrice
                                            fontSize="sm"
                                            baseDirection="row"
                                            currency={basket?.currency}
                                            showOnlyItemTotal={true}
                                        />
                                    </Flex>
                                </HideOnMobile>
                            </Stack>
                        </Flex>
                    </ItemVariantProvider>
                )
            })}
        </Stack>
    )
}

CartItems.propTypes = {
    basket: PropTypes.object,
    showHeading: PropTypes.bool
}

export default CartItems
